import { useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import PersonAddIcon from '@mui/icons-material/PersonAddAlt';
import EmailIcon from '@mui/icons-material/Mail';
import PageHeader from '../components/PageHeader';
import LoadingFallback from '../components/LoadingFallback';
import EmptyState from '../components/EmptyState';
import StatusChip from '../components/StatusChip';
import FormSection from '../components/FormSection';
import InviteDialog from '../components/InviteDialog';
import { api, apiErrorMessage } from '../api/client';
import { useFeedback } from '../contexts/FeedbackContext';
import { useAuth } from '../contexts/AuthContext';
import type { ScheduleEntry, Student, User } from '../api/types';
import dayjs from 'dayjs';

export default function StudentDetailPage() {
  const { id } = useParams();
  const studentId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { confirm, toast } = useFeedback();
  const { user } = useAuth();
  const [inviteOpen, setInviteOpen] = useState(false);

  const studentQuery = useQuery<Student>({
    queryKey: ['students', studentId],
    queryFn: async () => (await api.get(`/students/${studentId}`)).data,
  });

  const coachManagersQuery = useQuery<User[]>({
    queryKey: ['users', 'coach-managers'],
    queryFn: async () => {
      const [admins, coaches] = await Promise.all([
        api.get<User[]>('/users', { params: { role: 'admin' } }),
        api.get<User[]>('/users', { params: { role: 'coach' } }),
      ]);
      return [...admins.data, ...coaches.data].sort((a, b) => a.full_name.localeCompare(b.full_name));
    },
  });
  const parentsQuery = useQuery<User[]>({
    queryKey: ['users', 'parents'],
    queryFn: async () => (await api.get('/parents')).data,
  });

  const scheduleQuery = useQuery<{ entries: ScheduleEntry[] }>({
    queryKey: ['schedule', 'student', studentId],
    queryFn: async () => (await api.get(`/schedule/student/${studentId}`)).data,
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: Partial<Student>) =>
      (await api.patch(`/students/${studentId}`, payload)).data,
    onSuccess: () => {
      toast({ message: 'Saved', severity: 'success' });
      qc.invalidateQueries({ queryKey: ['students', studentId] });
      qc.invalidateQueries({ queryKey: ['students'] });
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const linkCoach = useMutation({
    mutationFn: async (uid: number) =>
      (await api.post(`/students/${studentId}/coaches/${uid}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['students', studentId] }),
  });
  const unlinkCoach = useMutation({
    mutationFn: async (uid: number) =>
      (await api.delete(`/students/${studentId}/coaches/${uid}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['students', studentId] }),
  });
  const linkParent = useMutation({
    mutationFn: async (uid: number) =>
      (await api.post(`/students/${studentId}/parents/${uid}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['students', studentId] }),
  });
  const unlinkParent = useMutation({
    mutationFn: async (uid: number) =>
      (await api.delete(`/students/${studentId}/parents/${uid}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['students', studentId] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => (await api.delete(`/students/${studentId}`)).data,
    onSuccess: () => {
      toast({ message: 'Athlete removed', severity: 'success' });
      navigate('/students');
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  if (studentQuery.isLoading || !studentQuery.data) return <LoadingFallback />;
  const s = studentQuery.data;
  const coachOptions = (coachManagersQuery.data ?? []).filter((c) => !s.coaches.find((sc) => sc.id === c.id));
  const parentOptions = (parentsQuery.data ?? []).filter((p) => !s.parents.find((sp) => sp.id === p.id));

  return (
    <Box>
      <PageHeader
        title={s.full_name}
        subtitle={`${s.grade ? `Grade ${s.grade} · ` : ''}${s.gender || ''}${s.bib_number ? ` · Bib ${s.bib_number}` : ''}`}
        actions={
          user?.role === 'admin' && (
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={async () => {
                const ok = await confirm({
                  title: 'Delete athlete?',
                  message: `Permanently delete ${s.full_name} and all their registrations?`,
                  destructive: true,
                  confirmLabel: 'Delete',
                });
                if (ok) deleteMutation.mutate();
              }}
              aria-label="Delete athlete"
            >
              Delete
            </Button>
          )
        }
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <FormSection title="Profile">
                <ProfileForm
                  student={s}
                  saving={updateMutation.isPending}
                  onSave={(p) => updateMutation.mutate(p)}
                />
              </FormSection>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <FormSection title="Coaches & admins" description="Team admins can coach too. Assign anyone who manages this athlete.">
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                  <Autocomplete
                    sx={{ flex: 1, minWidth: 240 }}
                    options={coachOptions}
                    getOptionLabel={(o) => `${o.full_name} (${o.email})`}
                    onChange={(_, val) => val && linkCoach.mutate(val.id)}
                    renderInput={(params) => (
                      <TextField {...params} label="Add coach or admin" inputProps={{ ...params.inputProps, 'aria-label': 'Add coach or admin' }} />
                    )}
                  />
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {s.coaches.length === 0 && <Typography color="text.secondary">No coaches or admins yet.</Typography>}
                  {s.coaches.map((c) => (
                    <Chip
                      key={c.id}
                      label={`${c.full_name}${c.role === 'admin' ? ' (admin)' : ''}`}
                      onDelete={() => unlinkCoach.mutate(c.id)}
                      aria-label={`Remove coach ${c.full_name}`}
                    />
                  ))}
                </Stack>
              </FormSection>

              <FormSection title="Parents / guardians" description="Receive schedule emails for this athlete.">
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                  <Autocomplete
                    sx={{ flex: 1, minWidth: 240 }}
                    options={parentOptions}
                    getOptionLabel={(o) => `${o.full_name} (${o.email})`}
                    onChange={(_, val) => val && linkParent.mutate(val.id)}
                    renderInput={(params) => (
                      <TextField {...params} label="Link existing parent" inputProps={{ ...params.inputProps, 'aria-label': 'Link parent' }} />
                    )}
                  />
                  <Button
                    startIcon={<PersonAddIcon />}
                    onClick={() => setInviteOpen(true)}
                    aria-label="Invite a new parent"
                  >
                    Invite new parent
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {s.parents.length === 0 && <Typography color="text.secondary">No parents linked yet.</Typography>}
                  {s.parents.map((p) => (
                    <Chip
                      key={p.id}
                      icon={<EmailIcon />}
                      label={`${p.full_name} (${p.email})`}
                      onDelete={() => unlinkParent.mutate(p.id)}
                      aria-label={`Remove parent ${p.full_name}`}
                    />
                  ))}
                </Stack>
              </FormSection>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Schedule</Typography>
              <Divider sx={{ mb: 2 }} />
              {scheduleQuery.isLoading ? (
                <LoadingFallback height={120} />
              ) : (scheduleQuery.data?.entries ?? []).length === 0 ? (
                <EmptyState title="No registrations yet" description="Register this athlete in events from a meet's page." />
              ) : (
                <Table size="small" aria-label="Athlete schedule">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Meet</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Heat / Lane</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(scheduleQuery.data?.entries ?? []).map((e) => (
                      <TableRow key={e.registration_id} hover>
                        <TableCell>{e.scheduled_start_at ? dayjs(e.scheduled_start_at).format('MMM D, h:mm A') : 'TBD'}</TableCell>
                        <TableCell>
                          <RouterLink to={`/meets/${e.meet_id}`} aria-label={`Open ${e.meet_name}`}>
                            {e.meet_name}
                          </RouterLink>
                        </TableCell>
                        <TableCell>{e.event_name} <Typography variant="caption" color="text.secondary">{e.division}</Typography></TableCell>
                        <TableCell>{e.heat_number ? `Heat ${e.heat_number}${e.lane ? ` · L${e.lane}` : ''}` : '—'}</TableCell>
                        <TableCell><StatusChip status={e.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={() => qc.invalidateQueries({ queryKey: ['students', studentId] })}
        defaultRole="parent"
        roles={['parent']}
        studentId={s.id}
        studentName={s.full_name}
      />
    </Box>
  );
}

function ProfileForm({
  student,
  saving,
  onSave,
}: {
  student: Student;
  saving: boolean;
  onSave: (p: Partial<Student>) => void;
}) {
  const [first, setFirst] = useState(student.first_name);
  const [last, setLast] = useState(student.last_name);
  const [grade, setGrade] = useState(student.grade || '');
  const [gender, setGender] = useState(student.gender || '');
  const [bib, setBib] = useState(student.bib_number || '');
  const [dob, setDob] = useState(student.dob || '');
  const [notes, setNotes] = useState(student.notes || '');

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          first_name: first,
          last_name: last,
          grade,
          gender,
          bib_number: bib,
          dob: dob || null,
          notes,
        } as any);
      }}
      aria-label="Edit profile form"
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField label="First name" value={first} onChange={(e) => setFirst(e.target.value)} required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Last name" value={last} onChange={(e) => setLast(e.target.value)} required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Grade" value={grade} onChange={(e) => setGrade(e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Gender" value={gender} onChange={(e) => setGender(e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Bib #" value={bib} onChange={(e) => setBib(e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Date of birth"
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Notes" multiline minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Grid>
      </Grid>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button type="submit" variant="contained" disabled={saving} aria-label="Save profile">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Stack>
    </form>
  );
}
